/**
 * Persona Studio Create/Edit page
 * Adapted from LivePreviewWizard with real API integration
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import clsx from 'clsx';

import { Button, Card, Input, Textarea, Tag, Icon, toast, AnimatedTick } from '@lib/ui';
import { Accordion, AccordionItem, AccordionButton, AccordionPanel } from '@lib/ui';
import { PersonaPreview, DecisionSelector } from '../../poc/wizards/components';
import type { WizardPersona, PersonaDecisions } from '../types';
import {
  defaultPersona,
  defaultVoice,
  defaultWorldview,
  defaultDecisions,
  TRAIT_SUGGESTIONS,
  SAMPLE_PROMPTS,
  DECISION_DIMENSIONS,
  personaToWizard,
} from '../types';
import { usePersona, useCreatePersona, useUpdatePersona } from '../hooks/usePersonaApi';

export default function PersonaStudioCreate() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  // API hooks
  const { persona: existingPersona, isLoading: isLoadingPersona } = usePersona(id);
  const { createPersona, isCreating } = useCreatePersona();
  const { updatePersona, isUpdating } = useUpdatePersona();

  // Form state
  const [persona, setPersona] = useState<Partial<WizardPersona>>(defaultPersona);
  const [selectedChannel, setSelectedChannel] = useState('LinkedIn');
  const [selectedTopic, setSelectedTopic] = useState<string>(SAMPLE_PROMPTS[0]);
  const [isCompleted, setIsCompleted] = useState(false);

  // Load existing persona in edit mode
  useEffect(() => {
    if (existingPersona) {
      setPersona(personaToWizard(existingPersona));
    }
  }, [existingPersona]);

  const updatePersonaState = (updates: Partial<WizardPersona>) => {
    setPersona((prev) => ({ ...prev, ...updates }));
  };

  const traits = persona.voice?.traits || [];
  const decisions = persona.decisions || defaultDecisions;

  const toggleTrait = (trait: string) => {
    const currentTraits = persona.voice?.traits || [];
    if (currentTraits.includes(trait)) {
      updatePersonaState({
        voice: {
          ...defaultVoice,
          ...persona.voice,
          traits: currentTraits.filter((t) => t !== trait),
        },
      });
    } else if (currentTraits.length < 5) {
      updatePersonaState({
        voice: {
          ...defaultVoice,
          ...persona.voice,
          traits: [...currentTraits, trait],
        },
      });
    }
  };

  const updateDecision = (key: keyof PersonaDecisions, value: string) => {
    updatePersonaState({
      decisions: {
        ...decisions,
        [key]: value,
      },
    });
  };

  // Key decisions to show in the personality section
  const keyDecisions = DECISION_DIMENSIONS.filter((d) =>
    ['confidence', 'formality', 'energy', 'callToAction'].includes(d.key)
  );

  const handleComplete = async () => {
    if (!persona.name?.trim()) {
      toast.error('Please enter a persona name');
      return;
    }

    try {
      const personaData = {
        name: persona.name,
        avatar: persona.avatar,
        tagline: persona.tagline,
        worldview: {
          coreBeliefs: persona.worldview?.coreBeliefs ?? [],
          values: persona.worldview?.values ?? [],
          audience: persona.worldview?.audience ?? '',
          purpose: persona.worldview?.purpose ?? '',
        },
        voice: {
          traits: persona.voice?.traits ?? [],
          description: persona.voice?.description ?? '',
          examplePhrases: persona.voice?.examplePhrases ?? [],
          avoidPhrases: persona.voice?.avoidPhrases ?? [],
        },
        decisions: decisions,
      };

      if (isEditMode && id) {
        await updatePersona(id, personaData);
        toast.success(`"${persona.name}" updated successfully!`);
      } else {
        await createPersona(personaData);
        toast.success(`"${persona.name}" created successfully!`);
      }

      setIsCompleted(true);
      setTimeout(() => {
        navigate('/persona-studio');
      }, 1500);
    } catch {
      toast.error(isEditMode ? 'Failed to update persona' : 'Failed to create persona');
    }
  };

  const handleCancel = () => {
    navigate('/persona-studio');
  };

  // Loading state for edit mode
  if (isEditMode && isLoadingPersona) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
        <Card skin="shadow" className="p-12 text-center">
          <div className="animate-pulse">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-200 dark:bg-dark-600" />
            <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-32 mx-auto" />
          </div>
        </Card>
      </div>
    );
  }

  // Completion screen
  if (isCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900 p-6">
        <Card skin="shadow" className="p-12 text-center max-w-md">
          <AnimatedTick color="success" size="xl" className="mx-auto mb-6" />
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
            {isEditMode ? 'Persona Updated!' : 'Persona Created!'}
          </h2>
          <p className="text-gray-500 dark:text-dark-400">
            "{persona.name}" is ready to start creating content.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-600 px-6 py-4 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {isEditMode ? 'Edit Persona' : 'Create Persona'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-dark-400">
              {isEditMode
                ? 'Update your persona settings'
                : 'Live preview wizard - see changes in real-time'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="flat" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              color="success"
              onClick={handleComplete}
              disabled={!persona.name?.trim() || isCreating || isUpdating}
            >
              {isCreating || isUpdating ? (
                <>
                  <span className="animate-spin mr-1">
                    <Icon name="refresh" size="md" />
                  </span>
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="check" size="md" />
                  <span className="ml-1">{isEditMode ? 'Save Changes' : 'Complete'}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Main content - split screen */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel - Form */}
        <div className="w-1/2 border-r border-gray-200 dark:border-dark-600 overflow-y-auto">
          <div className="p-6">
            <Accordion multiple defaultValue={['identity']}>
              {/* Identity Section */}
              <AccordionItem value="identity" className="mb-4">
                <Card skin="bordered" className="overflow-hidden">
                  <AccordionButton
                    className={({ open }) =>
                      clsx(
                        'w-full flex items-center justify-between p-4 text-left',
                        'hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors',
                        open && 'border-b border-gray-200 dark:border-dark-600'
                      )
                    }
                  >
                    {({ open }) => (
                      <>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">Identity</h3>
                          <p className="text-sm text-gray-500 dark:text-dark-400">
                            Name, avatar, tagline
                          </p>
                        </div>
                        <Icon
                          name="chevron-down"
                          size="md"
                          className={clsx(
                            'transition-transform duration-200',
                            open && 'rotate-180'
                          )}
                        />
                      </>
                    )}
                  </AccordionButton>
                  <AccordionPanel className="p-4 space-y-4">
                    <Input
                      label="Persona Name"
                      placeholder="e.g., Thought Leader"
                      value={persona.name || ''}
                      onChange={(e) => updatePersonaState({ name: e.target.value })}
                    />
                    <Textarea
                      label="Tagline"
                      placeholder="e.g., Shaping the future of content creation"
                      value={persona.tagline || ''}
                      onChange={(e) => updatePersonaState({ tagline: e.target.value })}
                      rows={2}
                    />
                    <Input
                      label="Target Audience"
                      placeholder="e.g., Tech professionals seeking career growth"
                      value={persona.worldview?.audience || ''}
                      onChange={(e) =>
                        updatePersonaState({
                          worldview: {
                            ...defaultWorldview,
                            ...persona.worldview,
                            audience: e.target.value,
                          },
                        })
                      }
                    />
                    <Input
                      label="Purpose"
                      placeholder="e.g., Empower through actionable insights"
                      value={persona.worldview?.purpose || ''}
                      onChange={(e) =>
                        updatePersonaState({
                          worldview: {
                            ...defaultWorldview,
                            ...persona.worldview,
                            purpose: e.target.value,
                          },
                        })
                      }
                    />
                  </AccordionPanel>
                </Card>
              </AccordionItem>

              {/* Personality Section */}
              <AccordionItem value="personality" className="mb-4">
                <Card skin="bordered" className="overflow-hidden">
                  <AccordionButton
                    className={({ open }) =>
                      clsx(
                        'w-full flex items-center justify-between p-4 text-left',
                        'hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors',
                        open && 'border-b border-gray-200 dark:border-dark-600'
                      )
                    }
                  >
                    {({ open }) => (
                      <>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            Personality
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-dark-400">
                            Traits and tone characteristics
                          </p>
                        </div>
                        <Icon
                          name="chevron-down"
                          size="md"
                          className={clsx(
                            'transition-transform duration-200',
                            open && 'rotate-180'
                          )}
                        />
                      </>
                    )}
                  </AccordionButton>
                  <AccordionPanel className="p-4 space-y-6">
                    {/* Traits */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
                        Personality Traits ({traits.length}/5)
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {TRAIT_SUGGESTIONS.map((trait) => (
                          <Tag
                            key={trait}
                            size="sm"
                            variant={traits.includes(trait) ? 'filled' : 'outlined'}
                            color={traits.includes(trait) ? 'primary' : 'neutral'}
                            className="cursor-pointer"
                            onClick={() => toggleTrait(trait)}
                          >
                            {trait}
                          </Tag>
                        ))}
                      </div>
                    </div>

                    {/* Decision selectors */}
                    <div className="space-y-6 pt-2">
                      {keyDecisions.map((dimension) => (
                        <DecisionSelector
                          key={dimension.key}
                          dimension={dimension}
                          value={decisions[dimension.key]}
                          onChange={updateDecision}
                          compact
                        />
                      ))}
                    </div>
                  </AccordionPanel>
                </Card>
              </AccordionItem>

              {/* Voice Section */}
              <AccordionItem value="voice" className="mb-4">
                <Card skin="bordered" className="overflow-hidden">
                  <AccordionButton
                    className={({ open }) =>
                      clsx(
                        'w-full flex items-center justify-between p-4 text-left',
                        'hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors',
                        open && 'border-b border-gray-200 dark:border-dark-600'
                      )
                    }
                  >
                    {({ open }) => (
                      <>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">Voice</h3>
                          <p className="text-sm text-gray-500 dark:text-dark-400">
                            Speaking style and phrases
                          </p>
                        </div>
                        <Icon
                          name="chevron-down"
                          size="md"
                          className={clsx(
                            'transition-transform duration-200',
                            open && 'rotate-180'
                          )}
                        />
                      </>
                    )}
                  </AccordionButton>
                  <AccordionPanel className="p-4 space-y-4">
                    <Textarea
                      label="Voice Description"
                      placeholder="Describe how this persona communicates..."
                      value={persona.voice?.description || ''}
                      onChange={(e) =>
                        updatePersonaState({
                          voice: {
                            ...defaultVoice,
                            ...persona.voice,
                            description: e.target.value,
                          },
                        })
                      }
                      rows={3}
                    />
                    <Textarea
                      label="Example Phrases (one per line)"
                      placeholder="Phrases this persona would use..."
                      value={persona.voice?.examplePhrases?.join('\n') || ''}
                      onChange={(e) =>
                        updatePersonaState({
                          voice: {
                            ...defaultVoice,
                            ...persona.voice,
                            examplePhrases: e.target.value.split('\n').filter(Boolean),
                          },
                        })
                      }
                      rows={2}
                    />
                    <Textarea
                      label="Phrases to Avoid (one per line)"
                      placeholder="Phrases this persona would never use..."
                      value={persona.voice?.avoidPhrases?.join('\n') || ''}
                      onChange={(e) =>
                        updatePersonaState({
                          voice: {
                            ...defaultVoice,
                            ...persona.voice,
                            avoidPhrases: e.target.value.split('\n').filter(Boolean),
                          },
                        })
                      }
                      rows={2}
                    />
                  </AccordionPanel>
                </Card>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        {/* Right panel - Preview */}
        <div className="w-1/2 bg-gray-100 dark:bg-dark-850 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Topic selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
                Sample Topic
              </label>
              <div className="flex flex-wrap gap-2">
                {SAMPLE_PROMPTS.map((prompt) => (
                  <Button
                    key={prompt}
                    variant={prompt === selectedTopic ? 'filled' : 'soft'}
                    className="text-xs px-2 py-1"
                    onClick={() => setSelectedTopic(prompt)}
                  >
                    {prompt}
                  </Button>
                ))}
              </div>
            </div>

            {/* Live preview */}
            <PersonaPreview
              persona={persona}
              selectedChannel={selectedChannel}
              topic={selectedTopic}
              onChannelChange={setSelectedChannel}
            />

            {/* Suggestions */}
            {!persona.name && (
              <Card
                skin="bordered"
                className="p-4 bg-info-50 dark:bg-info-900/20 border-info-200 dark:border-info-800"
              >
                <div className="flex items-start gap-3">
                  <div className="text-info-600 dark:text-info-400 shrink-0 mt-0.5">
                    <Icon name="sparkles" size="md" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-info-800 dark:text-info-200">
                      Start by adding a name
                    </p>
                    <p className="text-sm text-info-600 dark:text-info-300 mt-1">
                      The preview will update as you fill in the form on the left.
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
